import type { CommentGenerator } from "../interfaces/comment-generation";
import type { GeminiClientImpl } from "../summary/ai-client";
import {
	type Comment,
	type CommenterDefinition,
	type CommenterType,
	type ConversationContext,
	DEFAULT_BASIC_POLICY,
	DEFAULT_COMMENTER_DEFINITIONS,
} from "../types/core";

/**
 * Comment generator that uses LLM processing with autonomous role selection.
 */
export class HybridCommentGenerator implements CommentGenerator {
	private geminiClient: GeminiClientImpl | null = null;
	private apiKey: string | null = null;
	private basicPolicy: string = DEFAULT_BASIC_POLICY;
	private commentMaxCount = 3;
	private commenterDefinitions: CommenterDefinition[] = [
		...DEFAULT_COMMENTER_DEFINITIONS,
	];

	public setApiKey(key: string): void {
		this.apiKey = key;
	}

	public setCommenterDefinitions(definitions: CommenterDefinition[]): void {
		this.commenterDefinitions = definitions;
	}

	public setBasicPolicy(policy: string): void {
		this.basicPolicy = policy;
	}

	public setCommentMaxCount(count: number): void {
		this.commentMaxCount = count;
	}

	public setGeminiClient(client: GeminiClientImpl): void {
		this.geminiClient = client;
	}

	private async getGeminiClient(): Promise<GeminiClientImpl> {
		if (this.geminiClient) return this.geminiClient;
		const { GeminiClientImpl } = await import("../summary/ai-client.js");
		this.geminiClient = new GeminiClientImpl();
		return this.geminiClient;
	}

	/**
	 * Generates multiple comments using LLM-based role selection
	 */
	async generateComments(
		context: ConversationContext,
	): Promise<Comment[] | null> {
		if (!this.apiKey) {
			throw new Error(
				"APIキーが設定されていません。設定画面から設定してください。",
			);
		}

		return await this.generateLLMComments(context);
	}

	/**
	 * Generates multiple comments using LLM approach with autonomous role selection
	 */
	private async generateLLMComments(
		context: ConversationContext,
	): Promise<Comment[] | null> {
		if (!this.apiKey) {
			throw new Error("APIキーが設定されていません。");
		}

		// Use configured max count
		let maxCount = this.commentMaxCount;

		// Clamp to valid range: at least 1, and at most the number of defined commenters or a reasonable limit (e.g. 10)
		maxCount = Math.max(
			1,
			Math.min(this.commenterDefinitions.length, maxCount),
		);

		try {
			// Prepare simplified commenter definitions for the prompt
			const commentersForPrompt = this.commenterDefinitions.map((d) => ({
				id: d.id,
				name: d.name,
				description: d.description,
			}));

			// Get client instance
			const client = await this.getGeminiClient();

			// Generate comments using LLM
			const results = await client.generateMultiComments(
				context,
				commentersForPrompt,
				this.apiKey,
				maxCount,
				this.basicPolicy,
			);

			if (!results || results.length === 0) {
				throw new Error("LLMからコメントが返されませんでした。");
			}

			// Create comment objects
			const comments: Comment[] = results.map((result) => {
				// Validate commenter ID against current definitions, fallback to 'logical_thinking' if invalid
				const isValidCommenter = this.commenterDefinitions.some(
					(rd) => rd.id === result.commenter,
				);
				const commenterId = isValidCommenter
					? (result.commenter as CommenterType)
					: ("logical_thinking" as CommenterType);

				return {
					id: `llm_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
					commenter: commenterId,
					content: result.content,
					timestamp: new Date(),
					context: { ...context },
				};
			});

			return comments;
		} catch (error) {
			console.error("LLM generation failed:", error);
			// Re-throw to be handled by caller
			throw error;
		}
	}

	/**
	 * Gets active commenters
	 */
	getActiveCommenters(): CommenterDefinition[] {
		return this.commenterDefinitions;
	}

	async compressContext(transcript: string): Promise<string | null> {
		if (!this.apiKey) return null;
		const client = await this.getGeminiClient();
		return await client.compressHistory(transcript, this.apiKey);
	}
}
